import { useNavigate } from "react-router-dom";
import { Container } from "../../../../core/components/Container";
import { Row } from "../../../../core/components/Row";
import { TextComponent } from "../../../../core/components/TextComponent";
import { ArrowRight } from "../../../../core/icons/ArrowRight";

interface SettingProps {
  icon?: React.ReactNode;
  title?: string;
  path?: string;
  children?: React.ReactNode;
  className?: string;
  showArrow?: boolean;
  status?:
    | "Unverified"
    | "Verified"
    | "Pending"
    | "Rejected"
    | "Live face check required";
  onClick?: () => void;
}

const STATUS_COLORS = {
  Unverified: "bg-red-200 text-red-600",
  Verified: "bg-green-200 text-green-600",
  Pending: "bg-white text-slate-500",
  Rejected: "bg-red-200 text-rose-600",
  "Live face check required": "bg-red-200 text-rose-600",
};

const SettingComponent: React.FC<SettingProps> = ({
  icon = null,
  title = "",
  path = "",
  children = null,
  className,
  showArrow = true,
  status,
  onClick,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (path) {
      navigate(path);
    }
  };

  const renderStatus = (
    status:
      | "Unverified"
      | "Verified"
      | "Pending"
      | "Rejected"
      | "Live face check required",
  ) => (
    <Container
      className={`${STATUS_COLORS[status]} flex items-center rounded-md font-semibold px-3 py-1`}
    >
      <TextComponent variant="h6" className="text-start font-medium">
        {status === "Rejected"
          ? "Rejected"
          : status === "Live face check required"
            ? "Live face check required"
            : status === "Unverified"
              ? "Unverified"
              : status === "Verified"
                ? "Verified"
                : "Under review"}
      </TextComponent>
    </Container>
  );

  return (
    <Container
      className={`w-full mb-3 md:mb-4 p-4 rounded-lg shadow-sm ${className} cursor-pointer`}
      onClick={handleClick}
      role="button"
      aria-label={title || "Menu item"}
    >
      <Row className="flex items-center justify-between rounded-md gap-3">
        <Row crossAxisAlignment="center" className="flex min-w-0 flex-1 items-center">
          {icon && (
            <Container className="bg-white shadow-md w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center text-primary">
              {icon}
            </Container>
          )}
          {title && (
            <TextComponent
              variant="h6"
              className="ml-3 md:ml-4 flex min-w-0 items-center whitespace-normal break-words font-semibold leading-tight"
            >
              {title}
            </TextComponent>
          )}
          {children && (
            <TextComponent
              variant="h6"
              className="ml-1 min-w-0 max-w-[9rem] overflow-hidden text-ellipsis whitespace-normal break-words leading-tight sm:max-w-xs"
            >
              {children}
            </TextComponent>
          )}
          {status && <div className="ml-3 md:ml-4">{renderStatus(status)}</div>}
        </Row>
        {showArrow && (
          <TextComponent
            variant="h6"
            className="font-medium ml-3 md:ml-4 flex items-center"
          >
            <ArrowRight />
          </TextComponent>
        )}
      </Row>
    </Container>
  );
};

export default SettingComponent;
